import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// The browser must never put a refreshed page back where it was. This runs when the file is first loaded, before React
// renders anything, so a phone browser has no chance to restore the old position first.
if (typeof window !== 'undefined') {
    try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'; } catch (e) { /* not available */ }
    // a reload starts from the top, and so does a page brought back from the back/forward cache
    window.addEventListener('beforeunload', () => window.scrollTo(0, 0));
    window.addEventListener('pageshow', (event) => { if (event.persisted) window.scrollTo(0, 0); });
    window.addEventListener('load', () => window.scrollTo(0, 0));
}

let frame = null;
// Our own smooth scroll to the top. It does not depend on the CSS scroll-behavior or on the phone's "reduce motion" setting,
// which many phones switch on through battery saver / "remove animations" and which made every scroll jump on mobile.
export const smoothScrollToTop = (duration = 480) => {
    const root = document.documentElement;
    const from = window.scrollY || root.scrollTop || 0;
    if (frame) cancelAnimationFrame(frame);
    if (from <= 0) return;

    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto'; // so each step is applied at once instead of being smoothed again by CSS
    const ease = t => 1 - Math.pow(1 - t, 3);
    const start = performance.now();
    const stop = () => { root.style.scrollBehavior = previous; frame = null; };
    const cancel = () => { if (frame) cancelAnimationFrame(frame); stop(); };
    // the person touching or wheeling the page takes control back
    window.addEventListener('touchstart', cancel, { once: true, passive: true });
    window.addEventListener('wheel', cancel, { once: true, passive: true });

    const step = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        window.scrollTo(0, Math.round(from * (1 - ease(progress))));
        if (progress < 1) frame = requestAnimationFrame(step);
        else { window.removeEventListener('touchstart', cancel); window.removeEventListener('wheel', cancel); stop(); }
    };
    frame = requestAnimationFrame(step);
};

// Every page opens at the top: a refresh no longer restores the old scroll position, and each route change
// scrolls smoothly back to the top (a #hash link scrolls smoothly to its target instead).
const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    const firstRender = useRef(true);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {
        const first = firstRender.current;
        firstRender.current = false;

        if (hash) {
            const target = document.getElementById(hash.slice(1));
            if (target) { target.scrollIntoView({ behavior: first ? 'auto' : 'smooth' }); return; }
        }
        if (first) window.scrollTo(0, 0);
        else smoothScrollToTop();
    }, [pathname, hash]);

    return null;
};

export default ScrollToTop;
